<template>
    <p class="sr-only" role="status">
        <template v-if="displayState === 'loading'">{{ t("peopleDirectory.teamSuggestions.loading") }}</template>
        <template v-else-if="displayState === 'empty'">{{ t("peopleDirectory.teamSuggestions.empty") }}</template>
        <template v-else-if="displayState === 'ready'">{{ t("peopleDirectory.teamSuggestions.ready", { count: suggestions.length }) }}</template>
    </p>
    <p class="sr-only" role="alert">
        <template v-if="displayState === 'error'">{{ rateLimited ? t("peopleDirectory.teamSuggestions.rateLimited") : t("peopleDirectory.teamSuggestions.error") }}</template>
    </p>
    <section ref="suggestionsSection" tabindex="-1" class="flex flex-col gap-4" :aria-label="t('peopleDirectory.teamSuggestions.title')" :aria-busy="displayState === 'loading'">
        <p v-if="displayState === 'ready'" class="body-small text-ink-muted flex items-start gap-2">
            <i class="bi bi-calendar3 shrink-0" aria-hidden="true" />
            {{ t("peopleDirectory.teamSuggestions.intro") }}
        </p>
        <BaseCard v-if="displayState !== 'ready'" padding="md" radius="lgr" theme="muted">
            <div class="flex items-start gap-3">
                <i
                    :class="['bi text-ink-muted', displayState === 'loading' ? 'bi-calendar3' : displayState === 'error' ? 'bi-arrow-clockwise' : 'bi-calendar2-check']"
                    aria-hidden="true" />
                <div class="flex min-w-0 flex-col items-start gap-2">
                    <template v-if="displayState === 'loading'">
                        <p class="body-small-semibold text-ink" aria-hidden="true">{{ t("peopleDirectory.teamSuggestions.loading") }}</p>
                        <p class="body-small text-ink-muted">{{ t("peopleDirectory.teamSuggestions.loadingHint") }}</p>
                    </template>
                    <template v-else-if="displayState === 'error'">
                        <p class="body-small-semibold text-ink">{{ t("peopleDirectory.teamSuggestions.errorTitle") }}</p>
                        <p class="body-small text-ink-muted" aria-hidden="true">
                            {{ rateLimited ? t("peopleDirectory.teamSuggestions.rateLimited") : t("peopleDirectory.teamSuggestions.error") }}
                        </p>
                        <BaseButton theme="secondary" size="sm" :disabled="disabled" @click="retry">{{ t("peopleDirectory.retry") }}</BaseButton>
                    </template>
                    <template v-else>
                        <p class="body-small-semibold text-ink">{{ t("peopleDirectory.teamSuggestions.emptyTitle") }}</p>
                        <p class="body-small text-ink-muted" aria-hidden="true">{{ t("peopleDirectory.teamSuggestions.empty") }}</p>
                    </template>
                </div>
            </div>
        </BaseCard>
        <p class="body-small text-ink-muted flex items-start gap-2 empty:sr-only" role="status">
            <template v-if="displayState === 'ready' && degraded"
                ><i class="bi bi-info-circle shrink-0" aria-hidden="true" />{{ t("peopleDirectory.teamSuggestions.fallback") }}</template
            >
        </p>
        <template v-if="displayState === 'ready'">
            <BaseCarousel
                :item-ids="suggestions.map((_, index) => String(index))"
                :items-per-view="isDesktop ? 2 : 1"
                :label="t('peopleDirectory.teamSuggestions.title')"
                :item-label="t('peopleDirectory.teamSuggestions.itemLabel')"
                show-count
                animated>
                <template v-for="(suggestion, index) in suggestions" :key="index" #[`item-${index}`]>
                    <BaseCard padding="sm" radius="lgr" class="h-full">
                        <div class="flex h-full flex-col gap-4">
                            <div class="flex flex-col gap-2">
                                <h3 class="body-semibold text-ink break-words">{{ suggestion.name }}</h3>
                                <p class="body-small text-ink-muted">{{ suggestion.reason }}</p>
                            </div>
                            <div class="mt-auto flex flex-col gap-3">
                                <ul class="flex flex-wrap gap-x-4 gap-y-2">
                                    <li v-for="member in suggestion.members" :key="member.id" class="flex max-w-full items-center gap-2">
                                        <PerspectiveAvatar v-if="member.candidate" :name="member.name" :letters="member.candidate.perspective_letters" size="2xs" />
                                        <BaseAvatar v-else size="2xs" disabled :initials="getAvatarInitials(member.name).charAt(0)" />
                                        <BaseTooltip v-if="!member.candidate" open-on-click>
                                            <template #trigger>
                                                <span tabindex="0" class="body-small text-ink-muted min-w-0 cursor-help break-words">{{ member.name }}</span>
                                            </template>
                                            <template #content>{{ t("peopleDirectory.teamSuggestions.notNadiaUser") }}</template>
                                        </BaseTooltip>
                                        <span v-else class="body-small text-ink-muted min-w-0 break-words">{{ member.name }}</span>
                                    </li>
                                </ul>
                                <div class="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
                                    <span v-if="isAdded(suggestion)" class="body-small text-ink flex items-center gap-2">
                                        <i class="bi bi-check-circle text-status-success" aria-hidden="true" />{{ t("peopleDirectory.teamSuggestions.added") }}
                                    </span>
                                    <span v-else class="scaffold-details-small text-ink-muted">{{
                                        t("peopleDirectory.teamSuggestions.colleagues", { count: suggestion.members.length })
                                    }}</span>
                                    <BaseButton
                                        theme="secondary"
                                        size="sm"
                                        :disabled="disabled"
                                        :aria-label="
                                            isAdded(suggestion)
                                                ? t('peopleDirectory.teamSuggestions.reviewNamed', { name: suggestion.name })
                                                : t('peopleDirectory.teamSuggestions.useNamed', { name: suggestion.name })
                                        "
                                        @click="isAdded(suggestion) ? emit('review') : select(suggestion)">
                                        <i :class="['bi', isAdded(suggestion) ? 'bi-arrow-right' : 'bi-plus-lg']" aria-hidden="true" />
                                        {{ isAdded(suggestion) ? t("peopleDirectory.teamSuggestions.review") : t("peopleDirectory.teamSuggestions.use") }}
                                    </BaseButton>
                                </div>
                            </div>
                        </div>
                    </BaseCard>
                </template>
            </BaseCarousel>
        </template>
        <p class="body-small text-ink flex items-start gap-2 empty:sr-only" role="status">
            <template v-if="displayState === 'ready' && selectionMessage"
                ><i class="bi bi-check-circle text-status-success shrink-0" aria-hidden="true" />{{ selectionMessage }}</template
            >
        </p>
        <DemoToolsLauncher v-if="demoToolsVisible" v-model:open="demoToolsOpen" :title="t('peopleDirectory.teamSuggestions.demoTitle')">
            <div class="flex flex-col gap-3">
                <h2 class="body-small-semibold text-ink">{{ t("peopleDirectory.teamSuggestions.demoTitle") }}</h2>
                <p class="body-small text-ink-muted">{{ t("peopleDirectory.teamSuggestions.demoHint") }}</p>
                <p class="body-small text-ink-muted">{{ t("peopleDirectory.teamSuggestions.regenerateHint") }}</p>
                <BaseButton theme="secondary" size="sm" :disabled="disabled || loading" @click="regenerate">
                    {{ t("peopleDirectory.teamSuggestions.regenerate") }}
                </BaseButton>
                <BaseButton
                    theme="secondary"
                    size="sm"
                    @click="
                        previewEmpty = !previewEmpty;
                        demoToolsOpen = false;
                    ">
                    {{ previewEmpty ? t("peopleDirectory.teamSuggestions.restoreRecommendations") : t("peopleDirectory.teamSuggestions.showEmpty") }}
                </BaseButton>
            </div>
        </DemoToolsLauncher>
    </section>
</template>

<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { TeamRecommendation, TeamRecommendations, TeamRecommendationsRequest } from "~vue/api/generated/team-perspective/types.gen";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseCarousel from "~vue/components/BaseCarousel.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import PerspectiveAvatar from "~vue/components/PerspectiveAvatar.vue";
import DemoToolsLauncher from "~vue/DemoTools/DemoToolsLauncher.vue";
import { getAvatarInitials } from "~vue/utils/avatarUtils";
import { computed, onMounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const {
    url,
    disabled = false,
    selectedMemberIds = [],
    selectedUnavailableIds = [],
    demoToolsVisible = true,
} = defineProps<{
    url: string;
    disabled?: boolean;
    selectedMemberIds?: string[];
    selectedUnavailableIds?: string[];
    demoToolsVisible?: boolean;
}>();
const emit = defineEmits<{ select: [suggestion: TeamRecommendation]; review: [] }>();
const { t } = useI18n();
const suggestionsSection = useTemplateRef<HTMLElement>("suggestionsSection");
const isDesktop = useBreakpoints(breakpointsTailwind).greaterOrEqual("md");
const loading = ref(false);
const failed = ref(false);
const rateLimited = ref(false);
const degraded = ref(false);
const suggestions = ref<TeamRecommendation[]>([]);
const selectionMessage = ref("");
const demoToolsOpen = ref(false);
const previewEmpty = ref(false);
const lastRequest = ref<TeamRecommendationsRequest>({});
const displayState = computed(() => {
    if (previewEmpty.value) {
        return "empty";
    }
    if (loading.value) {
        return "loading";
    }
    if (failed.value) {
        return "error";
    }
    return suggestions.value.length ? "ready" : "empty";
});

function isAdded(suggestion: TeamRecommendation) {
    return (
        suggestion.members.length > 0 &&
        suggestion.members.every((member) => (member.candidate ? selectedMemberIds.includes(member.candidate.id) : selectedUnavailableIds.includes(member.id)))
    );
}

async function load(payload: TeamRecommendationsRequest = {}) {
    if (loading.value || disabled) {
        return;
    }
    lastRequest.value = payload;
    loading.value = true;
    failed.value = false;
    rateLimited.value = false;
    try {
        const { data, error, response } = await useFetch(url).post(payload).json<TeamRecommendations>();
        if (error.value || !data.value) {
            failed.value = true;
            rateLimited.value = response.value?.status === 429;
            return;
        }
        suggestions.value = data.value.recommendations;
        degraded.value = data.value.degraded ?? false;
    } catch {
        failed.value = true;
    } finally {
        loading.value = false;
    }
}

function retry() {
    if (loading.value || disabled) {
        return;
    }
    suggestionsSection.value?.focus();
    void load(lastRequest.value);
}

function regenerate() {
    if (loading.value || disabled) {
        return;
    }
    demoToolsOpen.value = false;
    previewEmpty.value = false;
    selectionMessage.value = "";
    void load({ regenerate: true });
}

function select(suggestion: TeamRecommendation) {
    if (disabled || isAdded(suggestion)) {
        return;
    }
    emit("select", suggestion);
    selectionMessage.value = t("peopleDirectory.teamSuggestions.selectionHint");
}

onMounted(() => load());
</script>
